import { useState, useEffect, createContext, lazy, Suspense, useCallback } from "react";
import Auth from "./utils/auth";
import "./App.css";
import { useLazyQuery, useApolloClient } from "@apollo/client";
import { QUERY_USER } from "./graphQL/queries";
import { useLocation, useNavigate } from "react-router-dom";
import { Route, Routes } from "react-router";
import { useUserContext } from "./UserContext";
import CircularLoading from "./components/loading/CircularLoading";
import * as Sentry from "@sentry/react";
import reportError from "./utils/helpers/reportError";
import LoggedInCountdownTimer from "./layout/LoggedInCountdownTimer";
import {
  SESSION_EXPIRED_EVENT,
  resetSessionExpiredLogoutGuard,
} from "./utils/sessionExpiredLogout";
import { isCalculatorPath, isNpoEnrollmentPath, isUploadShellPath } from "./utils/publicShellPaths";
import { clearPostLoginRedirect, savePostLoginRedirect } from "./utils/postLoginRedirect";
import { ThemeProvider } from "@mui/material";
import useCustomTheme from "./themeConfig";
import DelayedFallback from "./components/loading/DelayedFallback";
const NavigationDrawer = lazy(() => import("./layout/NavigationDrawer"));
const ExternalRoutes = lazy(() => import("./navigation/ExternalRoutes"));
const DocumentUpload = lazy(() => import("./pages/externalPages/DocumentUpload"));
const ServiceRequestWidget = lazy(() => import("./components/serviceRequest/ServiceRequestWidget"));

export const UserContext = createContext();

export default function App() {
  const theme = useCustomTheme();
  var currentURL = new URL(window.location.href);
  var searchParams = new URLSearchParams(currentURL.search);
  let emailToken = searchParams.get("token");
  const { contextUserData, contextUserId, updateContextUser, setLoggedInUser, loggedInUser } =
    useUserContext();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [initialCountdown, setInitialCountdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  // NPO enrollment and calculator pages are public-facing and reachable both
  // with and without an authenticated session. Short-circuit to the external
  // shell for these paths so a logged-in user doesn't get bounced to the
  // dashboard by the in-app router's wildcard redirect.
  const isNpoEnrollmentRoute = isNpoEnrollmentPath(location.pathname);
  const isCalculatorRoute = isCalculatorPath(location.pathname);
  const isEmbeddablePublicRoute = isNpoEnrollmentRoute || isCalculatorRoute;
  const logoutAlertTime = 120;
  const handleLogout = useCallback(async () => {
    clearPostLoginRedirect();
    setIsLoggedIn(false);
    // Yield one tick so logged-in routes unmount before any in-flight refetches
    await new Promise((r) => setTimeout(r, 0));
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } catch (e) {
      // ignore network errors on logout
    } finally {
      Auth.logout();
      // Clear user-context state to avoid cross-user leakage
      try {
        setLoggedInUser(null);
      } catch (e) {}
      try {
        updateContextUser("");
      } catch (e) {}
      // Clear Apollo cache without refetching active queries
      try {
        await apolloClient.clearStore();
      } catch (e) {}
      navigate("/login", { replace: true });
      resetSessionExpiredLogoutGuard();
    }
  }, [apolloClient, navigate]);

  useEffect(() => {
    const onSessionExpired = () => {
      handleLogout();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [handleLogout]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      if (loggedInUser) {
        Sentry.setUser({
          id: loggedInUser?._id,
        });
      } else {
        Sentry.setUser(null);
      }
    }
  }, [loggedInUser]);

  const [queryUser, { loading, data, refetch }] = useLazyQuery(QUERY_USER);

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsCheckingSession(true);

        if (emailToken) {
          await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
          Auth.logout();
        }
        // Preflight: only hit GraphQL if we can authenticate via access or refresh
        let canQuery = false;
        try {
          const resp = await fetch("/api/auth/session", { credentials: "include" });
          const json = await resp.json().catch(() => ({}));
          const accessSec = json?.accessRemainingSec ?? 0;
          const refreshSec = json?.refreshRemainingSec ?? 0;
          if (resp.ok && json?.ok === true) {
            if (accessSec > 0) {
              canQuery = true;
            } else if (refreshSec > 0) {
              // Attempt refresh to obtain a fresh access
              try {
                const r = await fetch("/api/auth/refresh", {
                  method: "POST",
                  credentials: "include",
                });
                const j = await r.json().catch(() => ({}));
                canQuery = r.ok && j?.ok === true;
              } catch (e) {
                canQuery = false;
              }
            }
          }
        } catch (e) {
          canQuery = false;
        }

        if (!canQuery) {
          setIsLoggedIn(false);
          savePostLoginRedirect(
            window.location.pathname + window.location.search + window.location.hash
          );
          return;
        }

        const result = await queryUser({ variables: { sourcePath: window.location.pathname } });
        const hasUser = !!result?.data?.user;
        setIsLoggedIn(hasUser);
        if (!hasUser) {
          savePostLoginRedirect(
            window.location.pathname + window.location.search + window.location.hash
          );
        }
      } catch (error) {
        reportError("App.checkSession failed", error);
        setIsLoggedIn(false);
        savePostLoginRedirect(
          window.location.pathname + window.location.search + window.location.hash
        );
      } finally {
        setIsCheckingSession(false);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (data && data.user) {
      setLoggedInUser(data.user);
    }
  }, [data]);

  // Send an authenticated-but-unverified session to the verify page. The login
  // route (backend/routes/authRoutes.js) intentionally issues a full session for
  // the email-verification link flow BEFORE the email is marked verified, and
  // the client completes verification via a separate `validateEmail` mutation.
  // If that mutation fails or is interrupted, the user is left with a valid
  // session and emailVerified === false; this redirect (paired with the guard
  // below) keeps them out of the app shell until they verify.
  // Future hardening: have the token login mint a scoped, short-lived token
  // usable only for `validateEmail` and only issue the real session once the
  // email is verified. That would close the unverified-session window entirely
  // and make this client-side guard redundant.
  useEffect(() => {
    if (isEmbeddablePublicRoute) return;
    if (data?.user && data.user.emailVerified === false) {
      navigate("/verify-reminder", {
        replace: true,
        state: { email: data.user.email, userId: data.user._id },
      });
    }
  }, [data, isEmbeddablePublicRoute]);

  useEffect(() => {
    if (isLoggedIn) {
      // Initialize a 4-hour countdown; it will be reset on refresh via modal.
      setInitialCountdown(4 * 60 * 60);
      resetSessionExpiredLogoutGuard();
    }
  }, [isLoggedIn]);
  useEffect(() => {
    updateContextUser(contextUserId);
  }, [contextUserId]);

  function updateIsLoggedIn(newValue) {
    setIsLoggedIn(newValue);
  }

  const preventCrash = () => {
    alert(
      "There was an error displaying your account, please contact customer service at support@uicharitable.org"
    );
    handleLogout();
  };

  if (isEmbeddablePublicRoute) {
    return (
      <ThemeProvider theme={theme}>
        <Suspense fallback={<DelayedFallback />}>
          <ExternalRoutes />
        </Suspense>
      </ThemeProvider>
    );
  }

  if (isCheckingSession) {
    return <CircularLoading loading={true} fallback={true} />;
  }

  const serviceRequestWidget = (
    <Suspense fallback={null}>
      <ServiceRequestWidget />
    </Suspense>
  );

  // Public upload page is accessible regardless of auth state
  if (isUploadShellPath(window.location.pathname)) {
    return (
      <ThemeProvider theme={theme}>
        <Suspense fallback={<DelayedFallback />}>
          <Routes>
            <Route path="upload/:slug" element={<DocumentUpload />} />
          </Routes>
        </Suspense>
        {serviceRequestWidget}
      </ThemeProvider>
    );
  }

  // Defense-in-depth: render the external shell (never NavigationDrawer) for an
  // authenticated-but-unverified account, so no authenticated route is mounted
  // even via a direct URL. The effect above redirects it to /verify-reminder.
  if (isLoggedIn && data?.user && data.user.emailVerified === false) {
    return (
      <ThemeProvider theme={theme}>
        <Suspense fallback={<DelayedFallback />}>
          <ExternalRoutes />
        </Suspense>
        {serviceRequestWidget}
      </ThemeProvider>
    );
  }

  return isLoggedIn ? (
    loading && !data && !data?.user ? (
      (<CircularLoading loading={true} fallback={true} />) // Do not delay initial app loader
    ) : data && data.user ? (
      <ThemeProvider theme={theme}>
        <Suspense fallback={<DelayedFallback />}>
          {isLoggedIn && initialCountdown !== null && (
            <LoggedInCountdownTimer
              initialCountdown={initialCountdown}
              logoutAlertTime={logoutAlertTime}
              handleLogout={handleLogout}
            />
          )}
          <NavigationDrawer
            userId={contextUserData ? contextUserData._id : data.user._id}
            user={contextUserData ? contextUserData : data.user}
            refetchLoggedInUser={refetch}
            loggedInUserId={data.user._id}
            handleLogout={handleLogout}
          />
        </Suspense>
        {serviceRequestWidget}
      </ThemeProvider>
    ) : (
      <>{preventCrash()}</>
    )
  ) : (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<DelayedFallback />}>
        <ExternalRoutes />
      </Suspense>
      {serviceRequestWidget}
    </ThemeProvider>
  );
}
